import type { NotePriorityUpdate } from '@numeo/types-core'
import axios from 'axios'
import { driverNoteContextKey, DriverNoteActorType, DriverNoteDTO } from '@numeo/types'
import { NotePriority } from '@numeo/types-core'

/** Shared budget for note mutations and authoritative note recovery reads. */
export const NOTE_REQUEST_TIMEOUT_MS = 15_000

export type { DriverNoteDTO }
export { DriverNoteActorType }

export async function createDriverNote(
    driverId: string,
    payload: {
        noteId: string
        text: string
        priority?: NotePriority
        loadId?: string
        truckId?: string
        actorType?: DriverNoteActorType
    }
): Promise<DriverNoteDTO> {
    const response = await axios.post<DriverNoteDTO>(`/updater/driver-note/${driverId}/note`, payload, { timeout: NOTE_REQUEST_TIMEOUT_MS })
    return response.data
}

export async function updateDriverNote(
    driverId: string,
    noteId: string,
    text: string,
    version?: number,
    priority?: NotePriorityUpdate
): Promise<DriverNoteDTO> {
    const response = await axios.patch<DriverNoteDTO>(
        `/updater/driver-note/${driverId}/note/${noteId}`,
        { text, version, priority },
        { timeout: NOTE_REQUEST_TIMEOUT_MS }
    )
    return response.data
}

export async function deleteDriverNote(driverId: string, noteId: string, version?: number): Promise<DriverNoteDTO> {
    const response = await axios.delete<{ note: DriverNoteDTO }>(`/updater/driver-note/${driverId}/note/${noteId}`, {
        data: { version },
        timeout: NOTE_REQUEST_TIMEOUT_MS,
    })
    return response.data.note
}

export interface BatchDriverNoteItem {
    driverId: string
    loadId?: string
    truckId?: string
}

export async function fetchDriverNotesBatch(items: BatchDriverNoteItem[]): Promise<Record<string, DriverNoteDTO[]>> {
    const response = await axios.post<{ results: Record<string, DriverNoteDTO[]> }>(
        '/updater/driver-note/batch',
        { items, limit: 50, keyFormat: 'json' },
        { timeout: NOTE_REQUEST_TIMEOUT_MS }
    )
    const results = response.data?.results ?? {}
    return Object.fromEntries(
        items.map(({ driverId, loadId, truckId }) => {
            const key = driverNoteContextKey(driverId, loadId, truckId)
            return [key, results[key] ?? []]
        })
    )
}
